import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

import type { ConversationAssistantUIModes } from './analytics-properties-context/types';

export const ALLOWED_AI_MATE_SOURCES = [
	'AIMate',
	'smart-create',
	'CSMAI',
	'CSMAIEmbed',
	'rovoServiceAgent',
	'solutionComposer',
] as const;

/**
 * Type derived from the allowed sources array
 */
export type AIMateAnalyticsSource = (typeof ALLOWED_AI_MATE_SOURCES)[number];

export type SourceAndAttributes = {
	source: AIMateAnalyticsSource;
	attributes: Record<string, unknown>;
};

export type CitedProductList = {
	[productName: string]: number;
};
export type SourceProducts = {
	[productName: string]: number;
};
export type AIResultViewedConnectorInfo = {
	ari?: string;
	provider_id?: string;
	connector_product?: string;
	connector_type?: string;
	search_retrieval_type?: string;
	source_product?: string;
}[];
/**
 * AI MAU Analytics
 * https://hello.atlassian.net/wiki/spaces/CONVAI/pages/edit-v2/3892665561
 * https://hello.atlassian.net/wiki/spaces/AAI/pages/3770721410/Unified+event+Instrumentation+for+AI
 */
export type TrackAIEventTypes =
	| 'initiated'
	| 'viewed'
	| 'error'
	| 'dismissed'
	| 'actioned'
	| 'feedback'
	| 'adopted';
export type TrackAIEventPayload<TKey extends TrackAIEventTypes, TData = void> = {
	type: TKey;
	proactive?: boolean;
	userGenerated?: boolean;
	doesNotMeetMAUCriteria?: boolean;
	source?: string;
	subjectId?: string;
	messageId?: string;
	pageContext?: string;
	[key: string]: unknown;
} & (TData extends void ? {} : TData);

export type TrackAIInitiated = TrackAIEventPayload<'initiated'>;
export type TrackAIViewed = TrackAIEventPayload<
	'viewed',
	{
		citedProductList?: CitedProductList;
		connectorInfo?: AIResultViewedConnectorInfo;
		sourceProducts?: SourceProducts;
	}
>;
export type TrackAIError = TrackAIEventPayload<'error'>;
export type TrackAIDismissed = TrackAIEventPayload<'dismissed'>;
export type TrackAIActioned = TrackAIEventPayload<
	'actioned',
	{
		actionResult: string;
		citedProduct?: string | null;
		connectorInfo?: AIResultViewedConnectorInfo;
		sourceProduct?: string | null;
	}
>;
export type TrackAIFeedback = TrackAIEventPayload<'feedback', { feedbackResult: 'up' | 'down' }>;

export type RovoModifiedObject = {
	objectType:
		| 'work-item'
		| 'page'
		| 'space'
		| 'whiteboard'
		| 'slide'
		| 'database'
		| 'image'
		| 'maui-app'
		| 'team'
		| 'project'
		| 'jsm-project'
		| 'jsm-survey'
		| 'jsm-form'
		| 'pull-request'
		| 'blog';
	objectId: string;
};

/** AI MAU event used to track objects modified by Rovo */
export type TrackAIAdopted = TrackAIEventPayload<
	'adopted',
	{ rovoModifiedObjects: RovoModifiedObject[]; actionKey: string }
>;

export type TrackAIEventPayloads =
	| TrackAIInitiated
	| TrackAIViewed
	| TrackAIError
	| TrackAIDismissed
	| TrackAIActioned
	| TrackAIFeedback
	| TrackAIAdopted;

export type RemixSharedMetadata = {
	aiInvocationSource?: string;
	entryPoint?: string;
	invokedFrom?: string;
	singleInstrumentationID?: string;
	dynamicUiType?: string;
	dynamicUiSubtype?: string;
};

export type RemixResultAction = 'applied' | 'deleted' | 'reverted' | 'replaced' | 'restored';

export type RemixResultRegistrationAttributes = RemixSharedMetadata & {
	pageId: string;
	singleInstrumentationID: string;
	dynamicUiType: string;
	operation: 'create' | 'edit';
	isLiveDoc?: boolean;
	sourceResultId?: string;
};

export type RemixResultAttributes = RemixResultRegistrationAttributes & {
	localId?: string;
	resultId: string;
	elapsedMs: number;
};

export type RemixAIResultViewedMetadata = Pick<
	RemixSharedMetadata,
	'invokedFrom' | 'singleInstrumentationID'
> & {
	pageContext?: string;
	viewContext?: 'ephemeral-preview' | 'staging-area';
	attributes?: Pick<RemixSharedMetadata, 'dynamicUiType' | 'dynamicUiSubtype'> & {
		contentId?: string;
		actionKey?: string;
		entryPoint?: string;
		isTabVisible?: boolean;
	} & Record<string, unknown>;
};

export type RemixAIResultActionedMetadata = RemixAIResultViewedMetadata & {
	attributes?: {
		userAction?: 'insert' | 'view' | 'edit' | 'copy';
	};
};

export type AIMAUEventPayload = {
	actionSubject: string;
	action: string;
	actionSubjectId?: string;
	eventType: 'track';
	tags: string[];
	source?: string;
	attributes: {
		proactiveAIGenerated: 1 | 0;
		userGeneratedAI: 1 | 0;
		isAIFeature: 1;
		aiFeedbackResult?: 'up' | 'down';
		aiResultAction?: string;
		aiFeatureName: 'aiChat';
		messageId?: string;
		[key: string]: unknown;
	};
};

export type SendAnalyticsEvent = (
	payload: AnalyticsEventPayload | UIAnalyticsEvent,
	options?: { removeDefaultTags?: boolean },
) => void;

export type IUseAnalytics = {
	sendAnalyticsEvent: SendAnalyticsEvent;
	trackAIMAUEvent: (event: TrackAIEventPayloads) => void;
	mode: ConversationAssistantUIModes;
};
